import { EventType } from "@/analytics/analytics-events";
import { ToggleFixedStatusForSelectedScans } from "@/analytics/staging-area-workflows-events-properties";
import { selectRevisionEntityDescendantScans } from "@/data-preparation-tool/store/revision/revision-scan-selectors";
import { isEntityCluster } from "@/data-preparation-tool/utils/entity-utils";
import {
  computeFixedStatus,
  toggleFixedStatus,
} from "@/data-preparation-tool/utils/toggle-fixed-status";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { CircularProgress, FaroText } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  CaptureTreeEntityRevision,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { ListItemIcon, MenuItem, Stack } from "@mui/material";
import { JSX, useCallback, useMemo, useState } from "react";
import { ReactComponent as ReleaseIcon } from "../../../icons/anchor-basic-striketrough.svg";
import { ReactComponent as FixIcon } from "../../../icons/anchor-basic.svg";
import { useFixedScanCount } from "../../revision-contextual-toolbar";

type FixReleaseContextMenuActionItemProps = {
  entity: CaptureTreeEntityRevision;
  disabled?: boolean;
  onSuccess?(): void;
};

/**
 * @returns A context menu item to fix or release a capture.
 */
export function FixReleaseContextMenuActionItem({
  entity,
  disabled,
  onSuccess,
}: FixReleaseContextMenuActionItemProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();
  const { projectApiClient } = useApiClientContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const descendantScans = useAppSelector(
    selectRevisionEntityDescendantScans(entity.id),
  );
  const isClusterOrRoot = isEntityCluster(entity) || entity.parentId === null;

  const descendantScanIds = useMemo(
    () => descendantScans.map((scan) => scan.id),
    [descendantScans],
  );

  const fixedDescendantCount = useFixedScanCount(descendantScanIds);

  const isFixed = useMemo(() => {
    if (isClusterOrRoot) {
      return computeFixedStatus(fixedDescendantCount, descendantScanIds.length);
    }
    return entity.isFixed === true;
  }, [
    isClusterOrRoot,
    fixedDescendantCount,
    descendantScanIds.length,
    entity.isFixed,
  ]);

  const onToggleFixedStatus = useCallback(async () => {
    if (isProcessing) return;

    const captureIds = isClusterOrRoot ? descendantScanIds : [entity.id];

    Analytics.track<ToggleFixedStatusForSelectedScans>(
      EventType.toggleFixedStatusForSelectedScans,
      {
        via: "scan tree",
        fixed: !isFixed,
        selectedScanCount: captureIds.length,
      },
    );

    await toggleFixedStatus(
      dispatch,
      getState(),
      projectApiClient,
      isFixed,
      captureIds,
      setIsProcessing,
      onSuccess,
    );
  }, [
    isProcessing,
    dispatch,
    getState,
    projectApiClient,
    isFixed,
    entity.id,
    isClusterOrRoot,
    descendantScanIds,
    onSuccess,
  ]);

  let icon: JSX.Element;
  let description: string;

  if (isProcessing) {
    icon = <CircularProgress size={20} />;
  } else if (isFixed) {
    icon = <ReleaseIcon height={24} width={24} />;
  } else {
    icon = <FixIcon height={24} width={24} />;
  }

  if (isFixed) {
    description =
      "Allows position and orientation to change\nduring registration or manual adjustment.";
  } else {
    description =
      "Prevents changes to position and orientation\nduring registration or manual adjustment.";
  }

  return (
    <MenuItem
      key="fixRelease"
      onClick={onToggleFixedStatus}
      disabled={
        (disabled ?? false) || isProcessing || descendantScanIds.length === 0
      }
    >
      <Stack direction="column" spacing={1}>
        <Stack direction="row">
          <ListItemIcon>{icon}</ListItemIcon>
          <FaroText variant="labelL">
            {isFixed ? "Release capture positions" : "Fix capture positions"}
          </FaroText>
        </Stack>
        <FaroText
          variant="helpText"
          sx={{ whiteSpace: "pre-line", margin: 0, paddingLeft: 4.5 }}
        >
          {description}
        </FaroText>
      </Stack>
    </MenuItem>
  );
}
